import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { useOrgContext } from '@/contexts/orgContext';
import { HeadingMenuSelected } from '@tigergraph/app-ui-lib/typography';
import { ParagraphSmall, ParagraphXSmall } from 'baseui/typography';
import { Input } from '@tigergraph/app-ui-lib/input';
import { useRef, useEffect } from 'react';
import { Button } from '@tigergraph/app-ui-lib/button';
import { axiosController } from '@/lib/network';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { BaseSkeleton } from '@/components/BaseSkeleton';
import Logo from '@/pages/home/logo';
import invalidLogo from '@/pages/home/invalid-logo.svg';
import { showToast } from '@/components/styledToasterContainer';
import { getErrorMessage } from '@/utils/utils.ts';
import { AxiosError } from 'axios';
import { TableContainer } from '@/lib/styled.tsx';
import { useForm, Controller } from 'react-hook-form';
import { FormControl } from '@/components/FormControl';
import { getCustomerInfo, CustomerInfo, UpdateCustomerRequest } from '@/pages/admin/bill/api';
import { KIND } from 'baseui/toast';
import mediaQuery from '@/lib/breakpoints';

const NameRegexp = /^[a-zA-Z0-9][a-z0-9_-]{2,49}$/;
const NameError =
  'invalid name, the name must only contain lowercase characters, ‘-’, and ‘_’, and start with a letter or number';
const NameRules = {
  required: 'required',
  pattern: {
    message: NameError,
    value: NameRegexp,
  },
};

type FormData = {
  orgName: string;
  displayName: string;
  logoUrl: string;
  billingEmail: string;
  billingAddress: {
    line1: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
};

export default function OrgSettings() {
  const [css, theme] = useStyletron();
  const imgRef = useRef<HTMLImageElement>(null);
  const { currentOrg, setCurrentOrg } = useOrgContext();
  const queryClient = useQueryClient();

  const { data: customerInfo, isFetching: isBillingFetching } = useQuery<CustomerInfo, AxiosError>(
    'customer-info',
    getCustomerInfo
  );

  const {
    handleSubmit,
    formState: { errors, isValid },
    control,
    watch,
    setValue,
    getValues,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      orgName: currentOrg.org_name,
      displayName: currentOrg.org_display_name || '',
      logoUrl: currentOrg.logo_url || '',
      billingEmail: '',
      billingAddress: {
        line1: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      },
    },
  });

  const imgUrl = watch('logoUrl');
  const displayName = watch('displayName');
  const orgName = watch('orgName');
  const billingEmail = watch('billingEmail');
  const billingAddress = watch('billingAddress');

  const isAnyAddressFilled = Object.values(billingAddress).some((v) => v.trim() !== '');

  // Conditional validation: if any address field is filled, all become required
  const addressFieldRules = {
    validate: (value: string) => {
      const address = getValues('billingAddress');
      const isAnyFilled = Object.values(address).some((v) => v.trim() !== '');
      if (isAnyFilled && !value.trim()) {
        return 'Required when any address field is filled';
      }
      return true;
    },
  };

  // Initialize billing data when customer info is loaded
  useEffect(() => {
    if (customerInfo) {
      setValue('billingEmail', customerInfo.customer_email || '');
      if (customerInfo.address) {
        setValue('billingAddress', {
          line1: customerInfo.address.line1 || '',
          city: customerInfo.address.city || '',
          state: customerInfo.address.state || '',
          postalCode: customerInfo.address.postalCode || '',
          country: customerInfo.address.country || '',
        });
      }
    }
  }, [customerInfo, setValue]);

  async function updateOrg({ orgName, displayName, logoUrl, billingEmail, billingAddress }: FormData) {
    // Update organization settings
    let promises: Promise<any>[] = [];
    promises.push(
      axiosController.put(`/v2/org`, {
        orgName: orgName,
        orgDisplayName: displayName,
        logoUrl: logoUrl,
      })
    );

    // Update billing settings if changed
    const billingEmailChanged = billingEmail !== (customerInfo?.customer_email || '');
    const originalAddress = customerInfo?.address;
    const billingAddressChanged = Object.keys(billingAddress).some(
      (key: any) =>
        billingAddress[key as keyof typeof billingAddress] !==
        (originalAddress?.[key as keyof typeof originalAddress] || '')
    );
    if (billingEmailChanged || billingAddressChanged) {
      const billingData: UpdateCustomerRequest = {};

      if (billingEmailChanged) {
        billingData.customer_email = billingEmail;
      }

      if (billingAddressChanged) {
        billingData.address = billingAddress;
      }

      promises.push(axiosController.post('/v2/billing/customer/update', billingData));
    }

    await Promise.all(promises);
  }

  const updateOrgMutation = useMutation(updateOrg, {
    onSuccess: () => {
      showToast({
        kind: KIND.positive,
        message: 'Organization settings updated successfully.',
      });
      const newOrg = {
        ...currentOrg,
        ...{
          org_name: orgName,
          org_display_name: displayName,
          logo_url: imgUrl,
        },
      };

      setCurrentOrg(newOrg);

      queryClient.setQueryData<CustomerInfo>('customer-info', {
        customer_email: billingEmail,
        address: billingAddress,
      });
    },
    onError: (error: AxiosError) => {
      showToast({
        kind: KIND.negative,
        message: getErrorMessage(error),
      });
    },
  });

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFormSubmit = (data: FormData) => {
    updateOrgMutation.mutate(data);
  };

  const addressLabel = (label: string) =>
    isAnyAddressFilled ? (
      <span>
        {label} <span style={{ color: theme.colors['text.danger'] }}>*</span>
      </span>
    ) : (
      label
    );

  if (isBillingFetching) {
    return <BaseSkeleton height="400px" />;
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <TableContainer $style={{ height: 'auto' }}>
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          })}
        >
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            })}
          >
            <div>
              <HeadingMenuSelected>Basic Information</HeadingMenuSelected>
              <ParagraphSmall>These are basic details needed to set up your new organization.</ParagraphSmall>
            </div>

            <div>
              <FormControl label="Organization Name" error={errors?.orgName?.message} size="compact">
                <Controller
                  rules={NameRules}
                  control={control}
                  name="orgName"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!!errors?.orgName}
                      autoComplete="off"
                      overrides={{
                        Root: {
                          style: {
                            maxWidth: '100%',
                          },
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
              <ParagraphXSmall
                className={css({
                  color: `${theme.colors['text.secondary']}`,
                  marginTop: '-8px',
                })}
              >
                This is a unique organization identifier on TigerGraph Savanna.
              </ParagraphXSmall>
            </div>

            <div>
              <FormControl label="Display Name" error={errors?.displayName?.message} size="compact">
                <Controller
                  rules={{
                    required: 'required',
                    minLength: {
                      value: 3,
                      message: 'minimum length is 3',
                    },
                    maxLength: {
                      value: 50,
                      message: 'maximum length is 50',
                    },
                  }}
                  control={control}
                  name="displayName"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!!errors?.displayName}
                      autoComplete="off"
                      overrides={{
                        Root: {
                          style: {
                            maxWidth: '100%',
                          },
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
              <ParagraphXSmall
                className={css({
                  color: `${theme.colors['text.secondary']}`,
                  marginTop: '-8px',
                })}
              >
                If set, this name will be displayed to users for your organization.
              </ParagraphXSmall>
            </div>
          </div>

          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            })}
          >
            <div>
              <HeadingMenuSelected>Branding</HeadingMenuSelected>
              <ParagraphSmall>These are branding settings associated with your organization.</ParagraphSmall>
            </div>

            <div
              className={css({
                display: 'flex',
              })}
            >
              <div
                className={css({
                  borderRight: `1px solid ${theme.colors.divider}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  paddingRight: '48px',
                  width: 'min(100%, 397px)',
                  [mediaQuery.xsmall]: {
                    borderRight: 'none',
                  },
                })}
              >
                <FormControl label="Organization Logo URL">
                  {currentOrg.org_id ? (
                    <div
                      className={css({
                        borderRadius: '4px',
                        border: `1px solid ${theme.colors.divider}`,
                        background: `${theme.colors['background.alternative']}`,
                        height: '200px',
                        position: 'relative',
                      })}
                    >
                      <div
                        className={css({
                          display: 'flex',
                          justifyContent: 'center',
                          position: 'absolute',
                          top: '40%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        })}
                      >
                        <img
                          ref={imgRef}
                          onError={() => {
                            if (imgRef.current && imgUrl !== '') {
                              imgRef.current.src = invalidLogo;
                            }
                          }}
                          src={imgUrl}
                          className={css({
                            maxWidth: '100%',
                            height: '60px',
                            display: 'inline',
                          })}
                        />
                      </div>
                      <Controller
                        control={control}
                        name="logoUrl"
                        render={({ field }) => (
                          <Input
                            placeholder="Your logo URL"
                            {...field}
                            autoComplete="off"
                            value={imgUrl}
                            overrides={{
                              Root: {
                                style: {
                                  position: 'absolute',
                                  bottom: '0',
                                  borderBottomWidth: '0',
                                  borderLeftWidth: '0',
                                  borderRightWidth: '0',
                                  borderTopRightRadius: '0',
                                  borderTopLeftRadius: '0',
                                  maxWidth: '100%',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </div>
                  ) : (
                    <div>
                      <BaseSkeleton height="200px" width="397px" />
                    </div>
                  )}
                </FormControl>
              </div>
              <div
                className={css({
                  paddingLeft: '48px',
                  position: 'relative',
                  [mediaQuery.xsmall]: {
                    display: 'none',
                  },
                })}
              >
                <ParagraphXSmall>Display Logo</ParagraphXSmall>
                <div
                  className={css({
                    display: 'flex',
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    alignItems: 'center',
                  })}
                >
                  {currentOrg.org_id ? (
                    <>
                      <Logo />
                      <div
                        className={css({
                          minWidth: '1px',
                          height: '40px',
                          background: `${theme.colors.divider}`,
                          margin: '0 8px',
                        })}
                      ></div>
                      {imgUrl ? (
                        <img src={imgUrl} className={css({ height: '26px' })} />
                      ) : (
                        <div
                          className={css({
                            maxWidth: '120px',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            fontSize: '14px',
                          })}
                        >
                          {displayName}
                        </div>
                      )}
                    </>
                  ) : (
                    <BaseSkeleton height="60px" width="200px" />
                  )}
                </div>
              </div>
            </div>
            <ParagraphXSmall
              $style={{
                marginTop: '-16px',
              }}
            >
              If set, this logo will be what the users for your organization see and use to recognize the organization.
            </ParagraphXSmall>
          </div>

          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              maxWidth: '547px',
            })}
          >
            <div>
              <HeadingMenuSelected>Contact Information</HeadingMenuSelected>
            </div>

            <div>
              <FormControl label="Billing Email" size="compact">
                <Controller
                  control={control}
                  name="billingEmail"
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter billing email"
                      type="email"
                      autoComplete="off"
                      overrides={{
                        Root: {
                          style: {
                            width: '100%',
                          },
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
              <ParagraphXSmall
                className={css({
                  color: `${theme.colors['text.secondary']}`,
                  marginTop: '-8px',
                })}
              >
                Future invoice notifications will be sent to this email. Please note that the user must have sufficient
                permissions to view invoices.
              </ParagraphXSmall>
            </div>

            <div>
              <HeadingMenuSelected>Billing Address</HeadingMenuSelected>
            </div>

            <div>
              <FormControl
                label={addressLabel('Street Address')}
                error={errors?.billingAddress?.line1?.message}
                size="compact"
              >
                <Controller
                  control={control}
                  name="billingAddress.line1"
                  rules={addressFieldRules}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter street address"
                      error={!!errors?.billingAddress?.line1}
                      autoComplete="off"
                      overrides={{
                        Root: {
                          style: {
                            width: '100%',
                          },
                        },
                      }}
                    />
                  )}
                />
              </FormControl>

              <FormControl label={addressLabel('City')} error={errors?.billingAddress?.city?.message} size="compact">
                <Controller
                  control={control}
                  name="billingAddress.city"
                  rules={addressFieldRules}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter city"
                      error={!!errors?.billingAddress?.city}
                      autoComplete="off"
                      overrides={{
                        Root: {
                          style: {
                            width: '100%',
                          },
                        },
                      }}
                    />
                  )}
                />
              </FormControl>

              <FormControl
                label={addressLabel('State/Province')}
                error={errors?.billingAddress?.state?.message}
                size="compact"
              >
                <Controller
                  control={control}
                  name="billingAddress.state"
                  rules={addressFieldRules}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter state"
                      error={!!errors?.billingAddress?.state}
                      autoComplete="off"
                      overrides={{
                        Root: {
                          style: {
                            width: '100%',
                          },
                        },
                      }}
                    />
                  )}
                />
              </FormControl>

              <FormControl
                label={addressLabel('Postal Code')}
                error={errors?.billingAddress?.postalCode?.message}
                size="compact"
              >
                <Controller
                  control={control}
                  name="billingAddress.postalCode"
                  rules={addressFieldRules}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter postal code"
                      error={!!errors?.billingAddress?.postalCode}
                      autoComplete="off"
                      overrides={{
                        Root: {
                          style: {
                            width: '100%',
                          },
                        },
                      }}
                    />
                  )}
                />
              </FormControl>

              <FormControl
                label={addressLabel('Country')}
                error={errors?.billingAddress?.country?.message}
                size="compact"
              >
                <Controller
                  control={control}
                  name="billingAddress.country"
                  rules={addressFieldRules}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter country"
                      error={!!errors?.billingAddress?.country}
                      autoComplete="off"
                      overrides={{
                        Root: {
                          style: {
                            width: '100%',
                          },
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>

          <div className={css({ marginTop: '-16px' })}>
            <Button
              size="large"
              type="submit"
              disabled={!isValid || updateOrgMutation.isLoading}
              isLoading={updateOrgMutation.isLoading}
            >
              Update
            </Button>
          </div>
        </div>
      </TableContainer>
    </form>
  );
}
